/**
 * LES BLOCS DU PLANNING — module PUR (28/08/2026, refait le 30/08/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── La demande ─────────────────────────────────────────────────────────────
 *
 * RQ, 24/08/2026 : « Dans l'affichage des plannings, je voudrais que tu sépares
 * en blocs, d'un côté les postes d'accueil matin et après-midi, et de l'autre
 * les postes techniques, et encore à part le poste de validation biologique, et
 * encore à part le poste de RQ et le poste de suppléant RQ. » Puis : « Garde
 * avec la technique. RQ et suppléant avec Métro et SIL. Entretien et coursier
 * ensemble et à part. »
 *
 * ── ⚠️ LE REVIREMENT DU 30/08/2026, ET POURQUOI IL EST JUSTE ──────────────
 *
 * La première version listait les codes de poste À LA MAIN, bloc par bloc, au
 * motif que `categorie` ne disait pas ces regroupements. C'était vrai, et le
 * remède était mauvais : `P3_HORMONO`, créé deux jours plus tard, n'était dans
 * aucune liste et s'affichait sous « Autres postes » — une paillasse technique
 * rendue hors de la technique, sans un mot.
 *
 * RQ, 30/08/2026 : « ça m'embête car on catégorise déjà les postes, je voudrais
 * me baser sur cela pour l'affichage des blocs. » Elle a raison, et le bon
 * geste n'était pas de recopier l'organisation dans une seconde liste : c'était
 * de CORRIGER LA CATÉGORIE des cinq postes qui la contredisaient. Un poste créé
 * demain porte une catégorie dès sa création — le formulaire l'exige — donc il
 * ne peut plus naître sans bloc.
 *
 * Les cinq postes repointés le 30/08 (`categories-postes-blocs.js`) :
 *   · PLVPM        `preanalytique` → `reception`  (ses six jumeaux y étaient)
 *   · BIOLOGISTE   `qualite`       → `validation`  (la RQ la veut séparée)
 *   · METROSTOCK   `technique`     → `qualite`     (décision du 24/08)
 *   · SIL          `technique`     → `qualite`     (décision du 24/08)
 *   · ENTRETIEN    `support`       → `services`    (avec le coursier)
 *
 * ── ⚠️ UNE SEULE LISTE DE CATÉGORIES, PARTAGÉE ───────────────────────────
 *
 * Il en existait TROIS qui se contredisaient : celle du planning, celle de la
 * liste des postes (`technique · preanalytique · garde · support`) et celle de
 * la fiche de poste (`reception · preanalytique · technique · postanalytique`).
 * Le formulaire d'édition n'offrait donc pas `reception` — douze postes la
 * portaient pourtant. Rouvrir l'un d'eux et enregistrer l'aurait basculé en
 * `technique` sans le dire. Une seule liste, ici, et les écrans l'importent.
 *
 * ── ⚠️ UN POSTE DE CATÉGORIE INCONNUE NE DISPARAÎT PAS ───────────────────
 *
 * Il tombe dans « Autres postes », visible et nommé. Le faire disparaître le
 * ferait lire comme un poste qu'on n'a pas à tenir.
 */

export interface CategoriePoste {
  cle: string;
  /** l'étiquette de la fiche de poste et du formulaire */
  libelle: string;
  /** le titre du bloc dans le planning, quand il diffère de l'étiquette */
  bloc?: string;
  /** la raison d'être du regroupement, quand elle ne va pas de soi */
  note?: string;
}

/**
 * LA liste des catégories de poste, dans l'ordre d'affichage du planning.
 * Elle sert à trois choses : le bloc du planning, l'étiquette lisible, et les
 * choix du formulaire d'édition d'un poste.
 */
export const CATEGORIES_POSTE: CategoriePoste[] = [
  {
    cle: 'reception',
    libelle: 'Réception & prélèvement',
    bloc: 'Accueil & prélèvement',
    note: 'matin et après-midi ensemble — la séance ne fait pas deux métiers',
  },
  {
    cle: 'technique',
    libelle: 'Paillasse technique',
    bloc: 'Paillasses techniques',
    note: 'les paillasses du jour — la garde a son propre bloc depuis le 29/09/2026',
  },
  {
    /* ⚠️ À PART depuis le 29/09/2026 (RQ) : « donner une catégorie à part au
       poste de garde, comme ça je pourrais voir uniquement le planning de
       garde ». Elle était rangée dans la technique (cycle de travail
       technique, décision antérieure) : on ne pouvait pas la filtrer seule. */
    cle: 'garde',
    libelle: 'Garde de nuit',
    note: 'la nuit, 19 h → 7 h — à part pour pouvoir la lire seule',
  },
  {
    cle: 'validation',
    libelle: 'Validation biologique',
    note: 'à part, et seule : la validation est l’acte qui engage le résultat rendu',
  },
  {
    cle: 'qualite',
    libelle: 'Qualité, métrologie & informatique',
    note: 'la RQ et son suppléant avec la métrologie et le SIL — décision du 24/08/2026',
  },
  {
    cle: 'services',
    libelle: 'Entretien & transport',
    note: 'ensemble et à part',
  },
  {
    /* ⚠️ La RQ n'a pas tranché ce cas : les postes du dimanche sont un RÉGIME,
       pas une paillasse — DIMTECH couvre à lui seul ce que P2 à P7 se partagent
       en semaine. Les fondre dans la technique donnerait à lire douze paillasses
       là où le laboratoire n'en ouvre qu'une. Bloc à part, donc, et à confirmer. */
    cle: 'dimanche',
    libelle: 'Régime du dimanche',
    note: 'un régime, pas des paillasses — regroupement proposé, à confirmer',
  },
];

/** L'étiquette lisible d'une catégorie ; la clé brute si elle est inconnue. */
export function libelleCategorie(cle?: string | null): string {
  const c = String(cle || '').trim();
  return CATEGORIES_POSTE.find((x) => x.cle === c)?.libelle || c;
}

export const BLOC_AUTRES = 'autres';

/**
 * Le bloc d'un poste, d'après SA CATÉGORIE. `autres` si elle est vide ou
 * inconnue — jamais rien.
 */
export function blocDeCategorie(categorie?: string | null): string {
  const c = String(categorie || '').trim();
  return CATEGORIES_POSTE.some((x) => x.cle === c) ? c : BLOC_AUTRES;
}

export interface LignePlanning {
  cle: string;
  titre: string;
  sous: string;
  /** la catégorie du poste — c'est elle qui décide du bloc */
  categorie?: string | null;
}

export interface GroupePlanning {
  cle: string;
  libelle: string;
  note: string;
  lignes: LignePlanning[];
}

/**
 * Les lignes du planning, réparties en blocs.
 *
 * ⚠️ Un bloc VIDE n'est pas rendu. Afficher « Validation biologique » sans
 * aucune ligne dessous se lit comme un poste non pourvu, alors que c'est
 * seulement un poste inactif ou filtré.
 *
 * ⚠️ L'ordre DANS le bloc est celui des lignes reçues — c'est-à-dire le champ
 * `ordre` du poste, que la RQ règle elle-même. Un tri alphabétique remettrait
 * ACCUEILPM entre ACCUEIL3 et PLV1, et GARDE en tête des paillasses.
 */
export function grouperLignes(lignes: LignePlanning[]): GroupePlanning[] {
  const par = new Map<string, LignePlanning[]>();
  for (const l of lignes) {
    const b = blocDeCategorie(l.categorie);
    if (!par.has(b)) par.set(b, []);
    par.get(b)!.push(l);
  }

  const out: GroupePlanning[] = [];
  for (const c of CATEGORIES_POSTE) {
    const dedans = par.get(c.cle);
    if (!dedans || !dedans.length) continue;
    out.push({ cle: c.cle, libelle: c.bloc || c.libelle, note: c.note || '', lignes: dedans });
  }

  const restes = par.get(BLOC_AUTRES);
  if (restes && restes.length) {
    out.push({
      cle: BLOC_AUTRES,
      libelle: 'Autres postes',
      note: 'ces postes n’ont pas de catégorie connue — à corriger dans leur fiche',
      lignes: [...restes].sort((x, y) => x.cle.localeCompare(y.cle)),
    });
  }
  return out;
}
